import React, { useState } from 'react';
import InsightsPanel from './InsightsPanel';
import InsightDetail from './InsightDetail';

type InsightType = 'critical' | 'emerging' | 'positive';
type FrameworkType = 'TCF' | 'NCD';
type SeverityLevel = 'High' | 'Medium' | 'Low';

interface RecommendedAction {
  action: string;
  expectedImpact: string;
}

interface DetailedInsight {
  id: string;
  type: InsightType;
  title: string;
  framework: FrameworkType;
  frameworkDetail: string;
  frameworkDescription: string;
  severity: SeverityLevel;
  confidence: number;
  timePeriod: string;
  analysisPoints: string[];
  rootCauseAssessment: string;
  recommendedActions: RecommendedAction[];
}

// Mock data for the detailed insight
const mockInsightDetail: DetailedInsight = {
  id: 'INS-001',
  type: 'critical',
  title: '73% increase in complaints about unclear fee disclosures in new accounts',
  framework: 'TCF',
  frameworkDetail: 'OUTCOME 3: CLEAR INFORMATION',
  frameworkDescription: 'Customers are provided with clear information and are kept appropriately informed before, during and after the point of sale.',
  severity: 'High',
  confidence: 92,
  timePeriod: '30 days',
  analysisPoints: [
    'Pattern identified across 47 complaints in the last 30 days',
    '89% from new customers who opened accounts after March 15, 2025',
    'Common phrases: "hidden fees", "wasn\'t informed", "unexpected charges"',
    'Most affected product: Premium Banking Account (78% of cases)'
  ],
  rootCauseAssessment: 'The fee structure changes implemented on March 15 were not adequately communicated in onboarding materials. The new fee schedule appears on page 7 of terms & conditions but is not highlighted during sign-up. Call center staff also lack updated training on explaining these fees.',
  recommendedActions: [
    {
      action: 'Update digital onboarding flow to explicitly highlight new fee structure',
      expectedImpact: '60% reduction in related complaints'
    },
    {
      action: 'Create quick reference guide for call center staff about new fees',
      expectedImpact: 'Improved first-call resolution for fee questions'
    },
    {
      action: 'Send clarification email to all customers onboarded since March 15',
      expectedImpact: 'Proactive notification may reduce formal complaints'
    }
  ]
};

// NCD version of the same insight
const ncdInsightDetail: DetailedInsight = {
  ...mockInsightDetail,
  framework: 'NCD',
  frameworkDetail: 'CONSUMER UNDERSTANDING',
  frameworkDescription: 'Firms should ensure that all customer communications are clear, fair, and easy to understand.',
};

interface InsightsContainerProps {
  showDetailView?: boolean;
}

const InsightsContainer: React.FC<InsightsContainerProps> = ({ showDetailView = false }) => {
  const [selectedInsightId, setSelectedInsightId] = useState<string | null>(showDetailView ? 'INS-001' : null);
  
  const handleViewDetails = (id: string) => {
    setSelectedInsightId(id);
  };
  
  const handleBackToList = () => {
    setSelectedInsightId(null);
  };
  
  return (
    <div>
      {selectedInsightId ? (
        <InsightDetail
          {...mockInsightDetail}
          onBack={handleBackToList}
        />
      ) : (
        <InsightsPanel onViewDetails={handleViewDetails} />
      )}
    </div>
  );
};

export default InsightsContainer;